import React, { memo, useState, useCallback, useRef, useEffect } from "react";
import {
  EdgeProps,
  getBezierPath,
  EdgeLabelRenderer,
  BaseEdge,
  useNodes,
} from "@xyflow/react";
import { FlowEditorEdge, FlowEditorNode } from "@/store/agentsStore";
import { Input } from "@/components/ui/input";

interface EditableDefaultEdgeProps extends EdgeProps<FlowEditorEdge> {
  onUpdateEdgeCondition?: (edgeId: string, condition: string) => void;
}

export const EditableDefaultEdge = memo(
  ({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    source,
    data,
    markerEnd,
    onUpdateEdgeCondition,
  }: EditableDefaultEdgeProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [conditionText, setConditionText] = useState(
      data?.edge.condition || "",
    );
    const inputRef = useRef<HTMLInputElement>(null);
    const nodes = useNodes<FlowEditorNode>();

    // Find source node to check if it's a condition node
    const sourceNode = nodes.find((node) => node.id === source);
    const isFromConditionNode =
      sourceNode?.type === "CONDITION" || sourceNode?.type === "AGENT";

    const [edgePath, labelX, labelY] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [isEditing]);

    const handleDoubleClick = useCallback(() => {
      if (isFromConditionNode) {
        setIsEditing(true);
      }
    }, [isFromConditionNode]);

    const handleBlur = useCallback(() => {
      setIsEditing(false);
      if (onUpdateEdgeCondition && data?.edge.id) {
        onUpdateEdgeCondition(data.edge.id, conditionText);
      }
    }, [conditionText, data?.edge.id, onUpdateEdgeCondition]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          handleBlur();
        }
        if (e.key === "Escape") {
          setConditionText(data?.edge.condition || "");
          setIsEditing(false);
        }
      },
      [handleBlur, data?.edge.condition],
    );

    useEffect(() => {
      setConditionText(data?.edge.condition || "");
    }, [data?.edge.condition]);

    return (
      <>
        <BaseEdge path={edgePath} markerEnd={markerEnd} />
        {(data?.edge.condition || isFromConditionNode) && (
          <EdgeLabelRenderer>
            <div
              style={{
                position: "absolute",
                transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                pointerEvents: "all",
              }}
              className="nodrag nopan"
              onDoubleClick={handleDoubleClick}
            >
              {isEditing ? (
                <Input
                  ref={inputRef}
                  value={conditionText}
                  onChange={(e) => setConditionText(e.target.value)}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  className="min-w-[100px] h-7 px-2 py-1 text-xs"
                  placeholder="Enter condition..."
                />
              ) : (
                <div
                  className={`bg-white px-2 py-1 rounded text-xs border ${
                    isFromConditionNode
                      ? "border-purple-300 cursor-text hover:border-purple-400"
                      : "border-gray-200"
                  }`}
                  title={isFromConditionNode ? "Double-click to edit" : ""}
                >
                  {conditionText ||
                    (isFromConditionNode ? "Set condition" : "")}
                </div>
              )}
            </div>
          </EdgeLabelRenderer>
        )}
      </>
    );
  },
);

EditableDefaultEdge.displayName = "EditableDefaultEdge";
